import * as React from "react";
import {cx} from "emotion";

import "./credit-cards-panel.scss";

import {cs}                 from "@Common/react/chain-services";
import {consumeContext}     from "@Common/react/context";
import {Load2}              from "@Common/react/load2";
import {UseState}           from "@Common/react/use-state";
import {BubbleNoti}         from "@Common/ui-components/bubble-noti/bubble-noti";
import {ConfirmDialog}      from "@Common/ui-components/heavy/common/design2/dialogs/confirm-dialog/confirm-dialog";
import {ReCaptchaInvisible} from "@Common/ui-components/recaptcha/recaptcha";
import {SliderCheckbox}     from "@Common/ui-components/slider-checkbox/slider-checkbox";

import {ItemGrid1} from "../item-grid-1/item-grid-1";

import {AddCardModal}  from "./card-modals/add-card-modal";
import {EditCardModal} from "./card-modals/edit-card-modal";

export const AccountCreditCardsPanel = ({apis}) => cs(
    consumeContext("userLevels"),
    ["cards", ({}, next) => Load2({
        fetch: () => apis.getList(),
        next,
    })],

    ["noti", (_, next) => BubbleNoti({next})],

    ["recaptcha", ({userLevels}, next) => ReCaptchaInvisible({next, isInternalUser: userLevels?.internalUser})],
    ["addCardModal", (_, next) => AddCardModal  ({apisAddUpdate: apis.addUpdate, next})],
    ["editCardModal", (_, next) => EditCardModal({apisAddUpdate: apis.addUpdate, next})],
    ["settingDefaultCard", (_, next) => UseState({next})],
    ({cards, settingDefaultCard, noti, addCardModal, editCardModal, recaptcha}) => ItemGrid1({
        height: 156,
        items: cards.value?.map((card) => ({
            key: card.cardToken,
            render: () => (
                <div className="credit-cards-panel-5ll card" xd-qa-type="card" xd-qa-name={card.cardHolderName}>
                    <div className="content">

                        <div className="left">
                            <div className="top">
                                <div className="name" title={card.cardHolderName} xd-qa-type="text" xd-qa-name="Name">
                                    {card.cardHolderName}
                                </div>

                                {card.isExpiredCard && (
                                    <div className="error" xd-qa-type="label" xd-qa-name="This credit card is expired">
                                        This credit card is expired
                                    </div>
                                )}
                                <div className="actions">
                                    <a
                                        onClick={async () => editCardModal.show({
                                            card,
                                            recaptchaToken: (await recaptcha.execute())?.token,
                                            onDone: async ({makeDefault}) => {
                                                if (!makeDefault && card.isDefaultCard) {
                                                    const newDefaultCard = cards.value.find((c) => !c.isExpiredCard && !c.isDefaultCard);
                                                    if (newDefaultCard) {
                                                        await apis.makeCardDefault(newDefaultCard.cardToken);
                                                    }
                                                }
                                                noti.show({
                                                    message: "Success! Your card has been saved.",
                                                    color: "green",
                                                });
                                                await cards.reload();
                                            },
                                        })}
                                        xd-qa-type="link"
                                        xd-qa-name="Edit"
                                    >Edit</a>

                                    {cs(
                                        ["confirmDialog", ({}, next) => ConfirmDialog({next})],
                                        ["removing", (_, next) => UseState({next})],
                                        ({confirmDialog, removing}) => (
                                            <a
                                                className={cx({disabled: removing.value})}
                                                onClick={async () => {
                                                    if (removing.value) {
                                                        return;
                                                    }

                                                    const confirm = await confirmDialog.show({
                                                        actionName: "remove",
                                                        message: `This credit card will be deleted: ${card.cardHolderName}`,
                                                    })

                                                    if (confirm) {
                                                        removing.onChange(true);
                                                        await apis.deleteCard(card.cardToken);
                                                        noti.show({
                                                            message: "Success! Your card has been removed.",
                                                            color: "green",
                                                        });
                                                        await cards.reload();
                                                    }
                                                }}
                                                xd-qa-type="link"
                                                xd-qa-name="Remove"
                                            >Remove</a>
                                        )
                                    )}
                                </div>
                            </div>

                            <div className="bottom">
                                <div className="item">
                                    <div className="label" xd-qa-type="label" xd-qa-name="Exp. Date">
                                        Exp. Date
                                    </div>

                                    <div className="value" xd-qa-type="text" xd-qa-name={card.expiration}>
                                        {card.expiration}
                                    </div>
                                </div>
                                <div className="item">
                                    <div className="label" xd-qa-type="label" xd-qa-name="Card Ending">
                                        Card Ending
                                    </div>

                                    <div className="value" xd-qa-type="text" xd-qa-name={card.cardLast4}>
                                        {card.cardLast4}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="right">
                            <div className="top" xd-qa-type="icon" xd-qa-name="Card Image">
                                <img src={icons[card.cardType]} alt={card.cardType}/>
                            </div>

                            <div className="bottom">
                                <div className="text" xd-qa-type="label" xd-qa-name="Default">
                                    Default
                                </div>
                                {SliderCheckbox({
                                    value: settingDefaultCard.value ? (
                                        settingDefaultCard.value === card.cardToken
                                    ) : (
                                        card.isDefaultCard
                                    ),
                                    disabled: settingDefaultCard.value || card.isExpiredCard,
                                    onChange: async () => {
                                        const newDefaultCard = !card.isDefaultCard ? (
                                            card.cardToken
                                        ) : (
                                            cards.value.find((c) => !c.isExpiredCard && !c.isDefaultCard)?.cardToken
                                        );
                                        settingDefaultCard.onChange(newDefaultCard);
                                        await apis.makeCardDefault(newDefaultCard);
                                        await cards.reload();
                                        settingDefaultCard.onChange(null);
                                    },
                                    xdQaName: "Default Checkbox"
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            )
        })),
        placeholder: {
            message: "You can add unlimited credit cards to your wallet",
            action: {
                label: "ADD NEW CARD",
                onClick: async () => addCardModal.show({
                    recaptchaToken: (await recaptcha.execute()).token,
                    onDone: async ({makeDefault}) => {
                        await cards.reload();

                        noti.show({
                            message: "Success! Your card has been added.",
                            color: "green",
                        });
                    },
                }),
            }
        },
    }),
);

const icons = {
    "Visa": require("../../../common/credit-card-icons/visa.svg"),
    "Discover": require("../../../common/credit-card-icons/Discover_Card_logo.svg"),
    "American Express": require("../../../common/credit-card-icons/amex.svg"),
    "MasterCard": require("../../../common/credit-card-icons/mc.svg"),
};
